import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useFormik } from 'formik';
import * as Yup from "yup";
import { useSelector } from "react-redux";
import CheckPermissions from "../authorization/authorization";
import Base64Image from "../components/Base64Image/Base64Image";
import rotateLeftIcon from "../images/Rotate Left.png";
import rotateRightIcon from "../images/Rotate Right.png";
import zoomInIcon from "../images/Zoom In.png";
import zoomOutIcon from "../images/Zoom Out.png";
import "./OpenBase64ImageInPopup.scss";

const OpenBase64ImageInPopup = ({
  docVerifyPopUp,
  hideDocVerifyPopup,
  documentName,
  documentImage,
  backDocumentImage,
  width,
  document,
  documentFlag,
  applicationProgressId,
  objectionStatus,
  updateDocFlag,
  titleToUpdateFlag,
  documentRemarks,
  flag_document,
  flag_documentBack,
  parentService,
  verificationPermissionService = 'New Connection',
}) => {
  const [rotationAngle, setRotationAngle] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showRemarksInput, setShowRemarksInput] = useState(false);
  const { permissions } = useSelector((state) => state.auth);

  const isImageDocument = documentImage?.slice(0, 6) === "data:i";
  const isBackImageDocument = backDocumentImage?.slice(0, 6) === "data:i";

  const remarksFormik = useFormik({
    initialValues: { iv_remarks: '' },
    validationSchema: Yup.object().shape({
      iv_remarks: Yup.string()
        .required("Enter reason for objection.")
        .min(10, "Reason should be between 10 to 200 characters!")
        .max(200, "Reason should be between 10 to 200 characters!"),
    }),
    onSubmit: async (values) => {
      updateDocFlag(titleToUpdateFlag, -1, values.iv_remarks);
    }
  });

  const hasVerificationPermission = CheckPermissions(
    permissions,
    "General Services",
    verificationPermissionService,
    "Workflow Permissions",
    "Document Verification"
  );

  const showVerificationActions =
    hasVerificationPermission &&
    document &&
    (flag_document !== 1 && flag_document !== -1) &&
    (parentService === 'courtOrder'
      ? applicationProgressId === 0
      : (applicationProgressId === 1 || applicationProgressId === 21)) &&
    objectionStatus !== 1;

  const remarksClassName = documentRemarks === "Verified"
    ? "doc-viewer-dialog__remarks doc-viewer-dialog__remarks--verified"
    : "doc-viewer-dialog__remarks doc-viewer-dialog__remarks--rejected";

  const handleClose = () => {
    if (typeof hideDocVerifyPopup === 'function') {
      hideDocVerifyPopup(false);
    }
    setRotationAngle(0);
    setZoomLevel(1);
    setShowRemarksInput(false);
    remarksFormik.resetForm();
  };

  useEffect(() => {
    if (!docVerifyPopUp) {
      setRotationAngle(0);
      setZoomLevel(1);
      setShowRemarksInput(false);
    }
  }, [docVerifyPopUp]);

  return (
    <Dialog
      open={docVerifyPopUp}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      className="doc-viewer-dialog"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      PaperProps={{
        className: 'doc-viewer-dialog__paper',
        sx: { position: 'relative', zIndex: 1 },
      }}
    >
      <DialogTitle className="doc-viewer-dialog__title">
        <div className="doc-viewer-dialog__header">
          <div className="doc-viewer-dialog__heading-wrap">
            <h3 className="doc-viewer-dialog__heading">{documentName}</h3>
            {documentRemarks ? (
              <p className={remarksClassName}>Remarks: {documentRemarks}</p>
            ) : null}
          </div>
          {isImageDocument ? (
            <div className="doc-viewer-dialog__tools">
              <button type="button" className="doc-viewer-dialog__tool-btn" onClick={() => setRotationAngle(rotationAngle - 90)} aria-label="Rotate left">
                <img src={rotateLeftIcon} alt="" />
              </button>
              <button type="button" className="doc-viewer-dialog__tool-btn" onClick={() => setRotationAngle(rotationAngle + 90)} aria-label="Rotate right">
                <img src={rotateRightIcon} alt="" />
              </button>
              <button type="button" className="doc-viewer-dialog__tool-btn" onClick={() => setZoomLevel(zoomLevel + 0.1)} aria-label="Zoom in">
                <img src={zoomInIcon} alt="" />
              </button>
              <button type="button" className="doc-viewer-dialog__tool-btn" onClick={() => setZoomLevel(Math.max(zoomLevel - 0.1, 0.1))} aria-label="Zoom out">
                <img src={zoomOutIcon} alt="" />
              </button>
            </div>
          ) : null}
          <button
            type="button"
            className="doc-viewer-dialog__close"
            onClick={handleClose}
            aria-label="Close document viewer"
          >
            <CloseIcon />
          </button>
        </div>
      </DialogTitle>

      <DialogContent className="doc-viewer-dialog__content">
        {backDocumentImage ? <p className="doc-viewer-dialog__section-label">Front</p> : null}
        {documentImage && isImageDocument ? (
          <div className="doc-viewer-dialog__media-wrap">
            <Base64Image
              data={documentImage}
              width={width}
              height="auto"
              rotationAngle={rotationAngle}
              zoomLevel={zoomLevel}
            />
          </div>
        ) : documentImage && documentImage.slice(0, 6) === "data:a" ? (
          <embed
            src={documentImage}
            type="application/pdf"
            className="doc-viewer-dialog__pdf"
            style={{ height: backDocumentImage ? "420px" : "640px" }}
          />
        ) : null}

        {backDocumentImage ? <p className="doc-viewer-dialog__section-label">Back</p> : null}
        {backDocumentImage && isBackImageDocument ? (
          <div className="doc-viewer-dialog__media-wrap">
            <Base64Image
              data={backDocumentImage}
              width={width}
              height="auto"
              rotationAngle={rotationAngle}
              zoomLevel={zoomLevel}
            />
          </div>
        ) : backDocumentImage && backDocumentImage.slice(0, 6) === "data:a" ? (
          <embed
            src={backDocumentImage}
            type="application/pdf"
            className="doc-viewer-dialog__pdf"
            style={{ height: "420px" }}
          />
        ) : null}
      </DialogContent>

      {showVerificationActions ? (
        <DialogActions className="doc-viewer-dialog__actions">
          {showRemarksInput ? (
            <div className="doc-viewer-dialog__reject-form">
              <div className="doc-viewer-dialog__reject-field">
                <label htmlFor="doc-viewer-remarks">Reason for rejection</label>
                <input
                  id="doc-viewer-remarks"
                  type="text"
                  name="iv_remarks"
                  value={remarksFormik.values.iv_remarks}
                  onBlur={remarksFormik.handleBlur}
                  onChange={remarksFormik.handleChange}
                />
                {remarksFormik.errors.iv_remarks && remarksFormik.touched.iv_remarks ? (
                  <p className="doc-viewer-dialog__field-error">{remarksFormik.errors.iv_remarks}</p>
                ) : null}
              </div>
              <button
                type="button"
                className="doc-viewer-dialog__btn doc-viewer-dialog__btn--accept"
                onClick={remarksFormik.handleSubmit}
              >
                Submit
              </button>
              <button
                type="button"
                className="doc-viewer-dialog__btn doc-viewer-dialog__btn--cancel"
                onClick={() => {
                  remarksFormik.resetForm();
                  setShowRemarksInput(false);
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="doc-viewer-dialog__action-row">
              <button
                type="button"
                className="doc-viewer-dialog__btn doc-viewer-dialog__btn--reject"
                onClick={() => setShowRemarksInput(true)}
              >
                Reject
              </button>
              <button
                type="button"
                className="doc-viewer-dialog__btn doc-viewer-dialog__btn--accept"
                onClick={() => updateDocFlag(titleToUpdateFlag, 1, 'Verified')}
              >
                Accept
              </button>
            </div>
          )}
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default OpenBase64ImageInPopup;
